import React, { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import truncate from 'lodash/truncate';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import EventDetails from '../../../components/events/EventDetails';
import config from '../../../config';
import strings from '../../../config/strings';
import { useEventApi } from '../../../hooks/api';
import { EventApi } from '../../../services/api';
import type {
  MetaResponse,
  PublicLotResponse,
} from '../../../services/api/types';
import { setPageTitle } from '../../../utils/base';
import Custom404 from '../../404';
type EventPageProps = MetaResponse & PublicLotResponse;
export default function EventPage(props: EventPageProps) {
  const queryClient = useQueryClient();
  const {
    query: { id },
  } = useRouter();

  const requestKey = `${config.queryKeys.vendor}.${id}`;
  const api = useEventApi();

  const { data, isError, isFetched, isLoading } = useQuery<
    PublicLotResponse,
    unknown
  >(
    requestKey,
    () => {
      return api.get(id as string);
    },
    { enabled: !props.entry }
  );

  useEffect(() => {
    if (props.entry) {
      queryClient.setQueryData(
        `${config.queryKeys.event}.${props.entry.nameId}`,
        {
          entry: props.entry,
        }
      );
    }
  }, [props.entry, queryClient]);

  const entry = useMemo(
    () => (isFetched && data?.entry ? data.entry : props.entry),
    [data?.entry, isFetched, props.entry]
  );

  if (isError || !entry?.id) {
    return <Custom404 />;
  }

  return (
    <>
      <Head>
        <title>
          {setPageTitle(props.title || strings.titles.eventDetails)}
        </title>

        {props.meta?.map((tag) => (
          <meta
            content={tag.content}
            key={tag.name || tag.property}
            name={tag.name}
            property={tag.property}
          />
        ))}
      </Head>

      {entry && <EventDetails entry={entry} isLoading={isLoading} />}
    </>
  );
}

// Disable static rendering
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id } = context.query;

  const api = new EventApi();
  const data: PublicLotResponse = await api.get(String(id));
  const { entry } = data;
  if (entry?.id) {
    const eventName = entry.uniqueName || entry.name;
    const location = entry?.location || '';

    const defaultImage =
      'https://res.cloudinary.com/foodease/image/upload/assets/logo.png';
    const description = truncate(entry.description, {
      length: 320,
    });
    const title = location
      ? `${eventName} | Food truck event in ${location}`
      : eventName;
    const timeUpdated = entry.timeUpdated || entry.timeCreated;
    const props: EventPageProps = {
      ...data,
      meta: [
        {
          name: 'description',
          content: description,
        },
        {
          property: 'fb:app_id',
          content: config.env.FACEBOOK_APP_ID,
        },
        // OpenGraph Meta Tags start
        {
          property: 'og:description',
          content: description,
        },

        {
          property: 'og:url',
          content: `https://order.goodfynd.com/events/${entry.nameId}`,
        },
        {
          property: 'og:image',
          content: entry.image || '',
        },

        {
          property: 'og:image:alt',
          content: defaultImage,
        },

        {
          property: 'og:title',
          content: title,
        },

        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:updated_time',
          content: (timeUpdated && new Date(timeUpdated).toISOString()) || '',
        },
        // OpenGraph Meta Tags end
        // Twitter Card Meta Tags start
        {
          name: 'twitter:creator',
          content: '@goodfynd',
        },

        {
          name: 'twitter:title',
          content: title,
        },

        {
          name: 'twitter:description',
          content: description,
        },

        {
          name: 'twitter:image',
          content: entry.image || defaultImage,
        },

        {
          name: 'twitter:card',
          content: 'summary',
        },

        {
          name: 'twitter:creator',
          content: '@goodfynd',
        },

        {
          name: 'twitter:site',
          content: '@goodfynd',
        },
        // Twitter Card Meta Tags end
      ].filter((tag) => tag.content),
      title,
    };

    return { props };
  }

  return { props: {} };
};
